import { Button, Input, Modal } from '../../../../../common/_custom';
import CircularSelect from '../../../../../common/MagicUpload/components/CircularSelect/CircularSelect';
import CountryCodesDropdown from '../../../../Company/CompanyLogin/CountryCodesDropdown/CountryCodesDropdown';
import { DIRECTOR_DESIGNATIONS } from './constants';
import useAddDirectorModalLogic from './useAddDirectorModalLogic';
import { AddDirectorModalProps } from './types';
import styles from './AddDirectorModal.module.scss';

function AddDirectorModal({ open, onClose, onSuccess, organizationId }: AddDirectorModalProps) {
    const { form, errors, saving, setField, handleClose, handleSave, getLabelStyle, fieldFocusHandlers, setPhoneCountryCode } =
        useAddDirectorModalLogic({ open, onClose, onSuccess, organizationId });

    // persistent: a backdrop (outside) click must NOT close the modal — a half-filled
    // add-director form shouldn't be lost to a stray click. The ✕ / Cancel path still
    // closes (Modal's handleClose only no-ops the overlay click when persistent).
    return (
        <Modal open={open} onClose={handleClose} size="flexible" persistent>
            {/* .AddDirectorModal/.Body relax overflow:hidden to visible while the Designation
                dropdown (CircularSelect) is focused, so its options menu can paint outside this
                box instead of being clipped at the modal/body edge (see AddDirectorModal.module.scss). */}
            <div className={styles.AddDirectorModal}>
                <div className={styles.Header}>
                    <h3 className={styles.Heading}>Add New</h3>
                </div>
                <div className={styles.Body}>
                    <div className={styles.FieldGroup}>
                        <Input
                            type="text"
                            placeholder="Name"
                            value={form.name}
                            onChange={(value: string) => setField('name', value)}
                            errorMessage={errors.name}
                            isValid={!errors.name}
                            containerClassName={styles.FieldContainer}
                            inputClassName={styles.FieldInput}
                            labelStyle={getLabelStyle('name', form.name)}
                            {...fieldFocusHandlers('name')}
                        />
                    </div>

                    <div>
                        <div className={styles.PanDinRow}>
                            <div className={styles.FieldGroup}>
                                <Input
                                    type="text"
                                    placeholder="PAN"
                                    value={form.pan}
                                    onChange={(value: string) => setField('pan', value)}
                                    errorMessage={errors.pan}
                                    isValid={!errors.pan}
                                    containerClassName={styles.FieldContainer}
                                    inputClassName={styles.FieldInput}
                                    labelStyle={getLabelStyle('pan', form.pan)}
                                    {...fieldFocusHandlers('pan')}
                                />
                            </div>
                            <div className={styles.OrDivider}>OR</div>
                            <div className={styles.FieldGroup}>
                                <Input
                                    type="text"
                                    placeholder="DIN"
                                    value={form.din}
                                    onChange={(value: string) => setField('din', value)}
                                    errorMessage={errors.din}
                                    isValid={!errors.din}
                                    containerClassName={styles.FieldContainer}
                                    inputClassName={styles.FieldInput}
                                    labelStyle={getLabelStyle('din', form.din)}
                                    {...fieldFocusHandlers('din')}
                                />
                            </div>
                        </div>
                        {errors.panOrDin && <div className={styles.PanOrDinError}>{errors.panOrDin}</div>}
                    </div>

                    <div className={styles.FieldGroup}>
                        <label className={styles.FieldLabel}>Designation (optional)</label>
                        <div className={styles.DesignationField}>
                            <CircularSelect
                                option={[...DIRECTOR_DESIGNATIONS]}
                                selectedOption={form.designation}
                                placeholder="Select Designation"
                                inputStyles={{
                                    height: '2.75rem',
                                    borderRadius: '0.375rem',
                                    border: '0.5px solid var(--border-color)',
                                    // CircularSelect's own default (peach empty-state /
                                    // grey selected-state) is styled via its module.scss;
                                    // this inline override wins the cascade without
                                    // touching that shared stylesheet — see
                                    // AddDirectorModal.module.scss .DesignationField for
                                    // the matching icon override.
                                    backgroundColor: 'var(--bg-color-white)',
                                    fontFamily: 'var(--text-gilroy-medium)',
                                    fontSize: '0.875rem',
                                    padding: '0.625rem 0.75rem',
                                }}
                                onSelect={(value: string) => {
                                    setField('designation', value);
                                    // Must resolve true — CircularSelect only commits a typed "Add Other" value
                                    // when onSelect resolves truthy (CircularSelect.tsx:201-208). The typed
                                    // designation is used on this record only, never persisted to the list.
                                    return Promise.resolve(true);
                                }}
                            />
                        </div>
                    </div>

                    <div className={styles.FieldGroup}>
                        <div className={styles.PhoneRow}>
                            <CountryCodesDropdown
                                isModal
                                setCountryCode={setPhoneCountryCode}
                                // @ts-ignore — CountryCodesDropdown has no props interface; `style` is untyped there too (see InviteTeammateModal/AddAssessee usage)
                                style={{
                                    width: '6rem',
                                    height: '2.75rem',
                                    borderRadius: '0.25rem',
                                    border: '0.5px solid #D2D2D2',
                                    margin: 0,
                                }}
                            />
                            <Input
                                type="text"
                                placeholder="Phone No (optional)"
                                value={form.phone}
                                onChange={(value: string) => setField('phone', value)}
                                errorMessage={errors.phone}
                                isValid={!errors.phone}
                                maxLength={10}
                                containerClassName={styles.PhoneFieldContainer}
                                inputClassName={styles.FieldInput}
                                labelStyle={getLabelStyle('phone', form.phone)}
                                {...fieldFocusHandlers('phone')}
                            />
                        </div>
                    </div>

                    <div className={styles.FieldGroup}>
                        <Input
                            type="text"
                            placeholder="Email (optional)"
                            value={form.email}
                            onChange={(value: string) => setField('email', value)}
                            errorMessage={errors.email}
                            isValid={!errors.email}
                            containerClassName={styles.FieldContainer}
                            inputClassName={styles.FieldInput}
                            labelStyle={getLabelStyle('email', form.email)}
                            {...fieldFocusHandlers('email')}
                        />
                    </div>
                </div>

                <div className={styles.Footer}>
                    <Button
                        text="Add"
                        variant="primary"
                        size="wide"
                        onClick={handleSave}
                        loading={saving}
                        disabled={saving}
                        className={styles.SaveButton}
                    />
                    <div className={styles.FooterNote}>Note: You cannot change  PAN and DIN after submission</div>
                </div>
            </div>
        </Modal>
    );
}

export default AddDirectorModal;
